<template>
  <Head title="Grafik Data Mahasiswa" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Grafik Data Mahasiswa', href: route('grafik.index') },
          ]"
        />

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <!-- Form Section -->
          <div
            class="order-2 col-span-1 space-y-6 lg:order-1"
            v-if="$page.props.auth.user"
          >
            <!-- Statistics Cards -->
            <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-green-500 to-blue-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-chart-pie sm:mr-3"></i>
                  Statistik
                </h2>
              </div>
              <div class="p-4 sm:p-6">
                <div class="grid grid-cols-1 gap-4">
                  <div class="flex items-center p-3 rounded-lg bg-blue-50">
                    <div class="p-2 text-blue-600 bg-blue-100 rounded-full">
                      <i class="w-5 h-5 fa-solid fa-users"></i>
                    </div>
                    <div class="ml-3">
                      <p class="text-sm font-medium text-gray-600">
                        Total Mahasiswa
                      </p>
                      <p class="text-xl font-bold text-gray-900">
                        {{ totalMahasiswa.toLocaleString() }}
                      </p>
                    </div>
                  </div>

                  <div class="flex items-center p-3 rounded-lg bg-green-50">
                    <div class="p-2 text-green-600 bg-green-100 rounded-full">
                      <i class="w-5 h-5 fa-solid fa-graduation-cap"></i>
                    </div>
                    <div class="ml-3">
                      <p class="text-sm font-medium text-gray-600">
                        Program Studi
                      </p>
                      <p class="text-xl font-bold text-gray-900">
                        {{
                          props.chartData && Array.isArray(props.chartData)
                            ? props.chartData.length
                            : 0
                        }}
                      </p>
                    </div>
                  </div>

                  <div class="flex items-center p-3 rounded-lg bg-purple-50">
                    <div class="p-2 text-purple-600 bg-purple-100 rounded-full">
                      <i class="w-5 h-5 fa-solid fa-database"></i>
                    </div>
                    <div class="ml-3">
                      <p class="text-sm font-medium text-gray-600">
                        Total Data
                      </p>
                      <p class="text-xl font-bold text-gray-900">
                        {{ grafiks.total }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-cog sm:mr-3"></i>
                  Aksi
                </h2>
              </div>
              <div class="p-4 sm:p-6">
                <div class="space-y-3">
                  <Link
                    :href="route('grafik.create')"
                    class="w-full inline-flex justify-center items-center px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                  >
                    <i class="mr-2 fa-solid fa-plus"></i>
                    Tambah Data
                  </Link>
                  <button
                    @click="exportData"
                    class="w-full inline-flex justify-center items-center px-4 py-2.5 text-sm font-semibold text-white bg-green-600 border border-transparent rounded-lg shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
                  >
                    <i class="mr-2 fa-solid fa-download"></i>
                    Export CSV
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Main Content Section -->
          <div class="order-1 col-span-1 lg:col-span-2 lg:order-2">
            <!-- Header Section -->
            <div class="mb-4 sm:mb-6">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    Grafik Data Mahasiswa
                  </h3>
                  <p class="mt-1 text-sm text-gray-600">
                    Visualisasi data mahasiswa per program studi dan semester
                  </p>
                </div>
                <div class="mt-2 text-sm text-gray-500 sm:mt-0">
                  Total: {{ grafiks.total }} data
                </div>
              </div>
            </div>

            <!-- Search Input -->
            <div class="relative mb-4">
              <div
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <MagnifyingGlassVue class="w-4 h-4 text-gray-400" />
              </div>
              <input
                v-model="form.search"
                @input="debounceFilter"
                type="text"
                autocomplete="off"
                placeholder="Cari program studi..."
                id="search"
                class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
              />
            </div>

            <!-- Success Message -->

            <!-- Data Table -->
            <div
              class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
            >
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 min-w-[150px]"
                      >
                        Data Grafik
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell min-w-[120px]"
                      >
                        Semester
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[120px]"
                      >
                        Jumlah Mahasiswa
                      </th>
                      <th
                        scope="col"
                        class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[100px]"
                      >
                        <span class="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="(grafik, index) in grafiks.data"
                      :key="grafik.id"
                      class="transition-colors duration-150 hover:bg-gray-50"
                    >
                      <td
                        class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                      >
                        {{
                          (grafiks.current_page - 1) * grafiks.per_page +
                          index +
                          1
                        }}
                      </td>
                      <td class="px-3 py-4 text-sm">
                        <div class="flex items-center">
                          <div class="flex-shrink-0 w-12 h-12 mr-3">
                            <div
                              class="flex items-center justify-center w-12 h-12 rounded-lg"
                              :class="[
                                grafik.semester === 'ganjil'
                                  ? 'bg-blue-100 text-blue-600'
                                  : 'bg-green-100 text-green-600',
                              ]"
                            >
                              <i class="fa-solid fa-chart-bar"></i>
                            </div>
                          </div>
                          <div class="flex-grow min-w-0">
                            <span
                              class="block font-medium text-gray-900 truncate"
                            >
                              {{
                                grafik.study_program?.name ||
                                `Prodi ${grafik.kode_prodi}`
                              }}
                            </span>
                            <div class="text-xs text-gray-500">
                              {{ grafik.tahun_akademik }} -
                              {{ grafik.kode_prodi }}
                            </div>
                            <!-- Mobile view: Show semester -->
                            <div class="mt-1 text-xs text-gray-500 md:hidden">
                              <span
                                :class="[
                                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                                  grafik.semester === 'ganjil'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-green-100 text-green-800',
                                ]"
                              >
                                {{
                                  grafik.semester.charAt(0).toUpperCase() +
                                  grafik.semester.slice(1)
                                }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        class="hidden px-3 py-4 text-sm text-gray-500 md:table-cell"
                      >
                        <span
                          :class="[
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                            grafik.semester === 'ganjil'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800',
                          ]"
                        >
                          {{
                            grafik.semester.charAt(0).toUpperCase() +
                            grafik.semester.slice(1)
                          }}
                        </span>
                      </td>
                      <td class="px-3 py-4 text-sm text-gray-500">
                        <div class="flex items-center space-x-2">
                          <span
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-800 bg-indigo-100 rounded-full"
                          >
                            <i class="mr-1 fa-solid fa-users"></i>
                            {{ grafik.jumlah_mahasiswa.toLocaleString() }}
                          </span>
                        </div>
                      </td>
                      <td
                        class="relative py-4 pl-3 pr-4 text-sm font-medium text-right sm:pr-6"
                      >
                        <div
                          class="flex items-center justify-end space-x-1 sm:space-x-2"
                        >
                          <!-- Action Buttons -->
                          <Link
                            :href="route('grafik.show', grafik.id)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 hover:text-blue-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="View"
                          >
                            <i class="w-3 h-3 fa-solid fa-eye"></i>
                            <span class="hidden ml-1 sm:inline">View</span>
                          </Link>
                          <Link
                            :href="route('grafik.edit', grafik.id)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-600 bg-yellow-100 rounded-md hover:bg-yellow-200 hover:text-yellow-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Edit"
                          >
                            <i class="w-3 h-3 fa-solid fa-edit"></i>
                            <span class="hidden ml-1 sm:inline">Edit</span>
                          </Link>
                          <button
                            @click="deleteGrafik(grafik.id)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 hover:text-red-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Delete"
                          >
                            <i class="w-3 h-3 fa-solid fa-trash"></i>
                            <span class="hidden ml-1 sm:inline">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Pagination -->
            <div class="flex items-center justify-between mt-6">
              <div class="flex items-center text-sm text-gray-500">
                Menampilkan {{ grafiks.from || 0 }} sampai
                {{ grafiks.to || 0 }} dari {{ grafiks.total }} data
              </div>
              <div class="flex items-center space-x-2">
                <component
                  :is="grafiks.prev_page_url ? 'Link' : 'span'"
                  :href="grafiks.prev_page_url"
                  class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 transition-colors duration-200 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  :class="{
                    'opacity-50 cursor-not-allowed': !grafiks.prev_page_url,
                  }"
                >
                  Previous
                </component>
                <template v-for="(link, index) in grafiks.links" :key="index">
                  <component
                    v-if="isNumber(link.label)"
                    :is="link.url ? 'Link' : 'span'"
                    :href="link.url"
                    class="inline-flex items-center px-3 py-2 text-sm font-medium transition-colors duration-200 border rounded-md cursor-pointer"
                    :class="
                      link.active
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                    "
                  >
                    {{ link.label }}
                  </component>
                </template>
                <component
                  :is="grafiks.next_page_url ? 'Link' : 'span'"
                  :href="grafiks.next_page_url"
                  class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 transition-colors duration-200 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  :class="{
                    'opacity-50 cursor-not-allowed': !grafiks.next_page_url,
                  }"
                >
                  Next
                </component>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from "vue";
import { Head, Link, router } from "@inertiajs/vue3";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import { Chart, registerables } from "chart.js";
import Swal from "sweetalert2";

Chart.register(...registerables);

// Props
const props = defineProps({
  grafiks: Object,
  filters: Object,
  tahunAkademikList: Array,
  prodiList: Array,
  chartData: Array,
});

// Reactive data
const form = ref({
  tahun_akademik: props.filters.tahun_akademik || "",
  semester: props.filters.semester || "",
  kode_prodi: props.filters.kode_prodi || "",
  search: props.filters.search || "",
});

const pieChart = ref(null);
const pieChartInstance = ref(null);
const debounceTimer = ref(null);

// Computed properties
const totalMahasiswa = computed(() => {
  // Debug untuk melihat struktur chartData
  console.log("Computing totalMahasiswa, chartData:", props.chartData);

  if (
    !props.chartData ||
    !Array.isArray(props.chartData) ||
    props.chartData.length === 0
  ) {
    console.warn("chartData is empty or invalid");
    return 0;
  }

  let total = 0;

  // Iterasi dan log setiap item untuk debugging
  props.chartData.forEach((item, index) => {
    if (item && typeof item === "object" && "total_mahasiswa" in item) {
      const value =
        typeof item.total_mahasiswa === "number"
          ? item.total_mahasiswa
          : parseInt(item.total_mahasiswa);

      if (!isNaN(value)) {
        console.log(`Item ${index}: Adding ${value} to total`);
        total += value;
      } else {
        console.warn(
          `Item ${index}: total_mahasiswa is NaN:`,
          item.total_mahasiswa
        );
      }
    } else {
      console.warn(`Item ${index}: Invalid structure:`, item);
    }
  });

  console.log("Calculated total:", total);
  return total;
});

// Helper method to check if link label is a number (for pagination)
const isNumber = (value) => {
  return !isNaN(value) && !isNaN(parseFloat(value));
};

// Methods
const filter = () => {
  router.get(route("grafik.index"), form.value, {
    preserveState: true,
    replace: true,
  });
};

const debounceFilter = () => {
  clearTimeout(debounceTimer.value);
  debounceTimer.value = setTimeout(() => {
    filter();
  }, 500);
};

const deleteGrafik = (id) => {
  Swal.fire({
    title: "Hapus Data Grafik?",
    text: "Data yang dihapus tidak dapat dikembalikan!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, Hapus!",
    cancelButtonText: "Batal",
    reverseButtons: true,
  }).then((result) => {
    if (result.isConfirmed) {
      router.delete(route("grafik.destroy", id), {
        onSuccess: () => {
          Swal.fire({
            title: "Berhasil!",
            text: "Data grafik berhasil dihapus.",
            icon: "success",
            confirmButtonColor: "#10b981",
            timer: 2000,
            timerProgressBar: true,
          });
        },
        onError: () => {
          Swal.fire({
            title: "Gagal!",
            text: "Terjadi kesalahan saat menghapus data.",
            icon: "error",
            confirmButtonColor: "#ef4444",
          });
        },
      });
    }
  });
};

const exportData = () => {
  const params = new URLSearchParams(form.value).toString();
  window.open(`/grafik-export?${params}`, "_blank");
};

const renderCharts = () => {
  renderPieChart();
};

const renderPieChart = () => {
  // Destroy existing chart instance if it exists
  if (pieChartInstance.value) {
    pieChartInstance.value.destroy();
    pieChartInstance.value = null;
  }

  // Ensure we have the canvas element
  if (!pieChart.value) {
    console.error("Pie chart canvas element not found");
    return;
  }

  console.log("Chart Data for pieChart:", props.chartData);

  // Ensure we have data to display
  if (
    !props.chartData ||
    !Array.isArray(props.chartData) ||
    props.chartData.length === 0
  ) {
    console.warn("No chart data available for pieChart");
    return;
  }

  // Ensure all data items are valid and have total_mahasiswa as number
  const sanitizedChartData = props.chartData.filter(
    (item) => item && typeof item === "object" && "total_mahasiswa" in item
  );

  if (sanitizedChartData.length === 0) {
    console.warn("No valid chart data items found");
    return;
  }

  const ctx = pieChart.value.getContext("2d");

  // Force clear the canvas to ensure no artifacts from previous renders
  ctx.clearRect(0, 0, pieChart.value.width, pieChart.value.height);

  // Check if we're visualizing a single study program's data by tahun_akademik & semester
  // This will be true when form.value.kode_prodi is a single value and the data has tahun_semester property
  const isSingleProdiView =
    form.value.kode_prodi &&
    !form.value.kode_prodi.includes(",") &&
    sanitizedChartData.length > 0 &&
    sanitizedChartData[0].tahun_semester;

  console.log("Is single prodi view:", isSingleProdiView);

  // Generate labels based on whether we're in single prodi view or not
  const labels = sanitizedChartData.map((item) => {
    if (isSingleProdiView) {
      return item.tahun_semester || `${item.tahun_akademik} ${item.semester}`;
    } else {
      return item.study_program?.name || `Prodi ${item.kode_prodi}`;
    }
  });

  const data = sanitizedChartData.map((item) => {
    // Ensure we're getting a valid number and log the conversion
    let value;
    if (typeof item.total_mahasiswa === "number") {
      value = item.total_mahasiswa;
      console.log(
        `Item ${item.kode_prodi || "unknown"}${
          isSingleProdiView ? " " + (item.tahun_semester || "") : ""
        }: Using number directly: ${value}`
      );
    } else {
      value = parseInt(String(item.total_mahasiswa));
      console.log(
        `Item ${item.kode_prodi || "unknown"}${
          isSingleProdiView ? " " + (item.tahun_semester || "") : ""
        }: Parsed from ${item.total_mahasiswa} to ${value}`
      );
    }

    if (isNaN(value)) {
      console.warn(
        `Item ${item.kode_prodi || "unknown"}${
          isSingleProdiView ? " " + (item.tahun_semester || "") : ""
        }: Invalid total_mahasiswa value: ${item.total_mahasiswa}`
      );
      return 0;
    }

    return value;
  });

  // Generate visually distinct colors for better readability
  const colors = [
    "#EF4444", // red
    "#3B82F6", // blue
    "#10B981", // green
    "#F59E0B", // amber
    "#8B5CF6", // purple
    "#EC4899", // pink
    "#06B6D4", // cyan
    "#84CC16", // lime
    "#6366F1", // indigo
    "#F97316", // orange
    "#14B8A6", // teal
    "#A855F7", // violet
    "#22C55E", // emerald
    "#F472B6", // fuchsia
    "#EAB308", // yellow
  ];

  // Ensure we have valid data to chart
  console.log("Final data for chart:", data);
  console.log("Labels for chart:", labels);

  if (data.length === 0 || labels.length === 0) {
    console.warn("No valid data points for chart");
    return;
  }

  // Ensure sum of data is not zero
  const totalSum = data.reduce((a, b) => a + b, 0);
  if (totalSum <= 0) {
    console.warn(
      "Total sum of data is zero or negative, cannot create pie chart"
    );
    return;
  }

  console.log("Creating new chart instance");

  try {
    console.log("Setting up chart with data:", { labels, data });

    // Create pie chart with enhanced options for better visibility
    pieChartInstance.value = new Chart(ctx, {
      type: "pie",
      data: {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: colors.slice(0, data.length),
            borderColor: "#ffffff",
            borderWidth: 2,
            hoverOffset: 15,
            weight: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "0%", // Makes it a full pie chart rather than donut
        radius: "90%", // Makes the chart slightly larger
        plugins: {
          legend: {
            position: "bottom",
            display: true,
            labels: {
              color: "#333",
              font: {
                size: 12,
                weight: "bold",
              },
              usePointStyle: true,
              padding: 15,
              boxWidth: 10,
              boxHeight: 10,
            },
          },
          tooltip: {
            enabled: true,
            backgroundColor: "rgba(0,0,0,0.8)",
            titleFont: {
              size: 14,
              weight: "bold",
            },
            bodyFont: {
              size: 13,
            },
            padding: 12,
            displayColors: true,
            callbacks: {
              label: function (context) {
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = ((context.raw / total) * 100).toFixed(1);

                // Check if we're in single prodi view
                const isSingleProdiView =
                  form.value.kode_prodi &&
                  !form.value.kode_prodi.includes(",") &&
                  props.chartData.length > 0 &&
                  props.chartData[0].tahun_semester;

                if (isSingleProdiView) {
                  // For single prodi view, show semester details
                  return `${
                    context.label
                  }: ${context.raw.toLocaleString()} mahasiswa (${percentage}%)`;
                } else {
                  // For multi prodi view, show program study name
                  return `${
                    context.label
                  }: ${context.raw.toLocaleString()} mahasiswa (${percentage}%)`;
                }
              },
            },
          },
        },
        animation: {
          animateRotate: true,
          animateScale: true,
          duration: 800,
        },
        elements: {
          arc: {
            borderWidth: 1,
            hoverBorderColor: "#fff",
          },
        },
      },
    });

    console.log("Chart instance successfully created:", pieChartInstance.value);
  } catch (error) {
    console.error("Error creating pie chart:", error);
  }

  console.log("Chart instance created:", pieChartInstance.value);
};

// Watch for chart data changes
watch(
  () => props.chartData,
  (newValue) => {
    console.log("Chart data changed, new length:", newValue?.length);
    console.log(
      "Is single prodi selected:",
      form.value.kode_prodi && !form.value.kode_prodi.includes(",")
    );

    if (newValue && newValue.length > 0) {
      console.log("First chart data item:", newValue[0]);
      console.log("Has tahun_semester:", Boolean(newValue[0].tahun_semester));
    }

    // Use nextTick to ensure DOM is ready
    nextTick(() => {
      renderCharts();
    });
  },
  { deep: true, immediate: true }
);

// Also watch filters changes
watch(
  () => props.filters,
  () => {
    console.log("Filters changed, will re-render charts");
    nextTick(() => {
      renderCharts();
    });
  },
  { deep: true }
);

// Lifecycle hooks
onMounted(() => {
  console.log(
    "Component mounted, initial chart data:",
    JSON.stringify(props.chartData)
  );

  if (props.chartData) {
    props.chartData.forEach((item, index) => {
      console.log(`Chart data item ${index}:`, item);
      if (item && "total_mahasiswa" in item) {
        console.log(
          ` - total_mahasiswa:`,
          item.total_mahasiswa,
          `(type: ${typeof item.total_mahasiswa})`
        );
      }
    });
  }

  // First render on next tick
  nextTick(() => {
    renderCharts();

    // Setup resize handler
    window.addEventListener("resize", handleResize);
  });
});

// Define resize handler as a named function
const handleResize = () => {
  if (pieChartInstance.value) {
    pieChartInstance.value.resize();
  }
};
</script>
